import {
  ONBOARDING_COMPLETED,
  ONBOARDING_STARTED,
  ONBOARDING_NOT_STARTED,
  COMPLETED,
} from "../config/CONSTANTS";

/**
 * Same 7/60 progress copy as OnboardingProgressBanner.
 * Returns null when that bar should not render.
 */
export const getOnboardingProgressMessage = ({
  userDetails,
  historicalJobStatusObject,
  sync7DaysStatusRaw,
  sync60DaysStatusRaw,
}) => {
  const baseMessage =
    "We are importing inventory and orders from Shopify to calculate forecast";

  const onboardingStatus =
    sync7DaysStatusRaw?.onboardingStatus ||
    sync60DaysStatusRaw?.onboardingStatus;
  if (
    onboardingStatus === COMPLETED ||
    onboardingStatus === ONBOARDING_COMPLETED
  ) {
    return null;
  }

  const syncOrderState = sync7DaysStatusRaw?.syncOrderState;
  const syncInventoryState = sync7DaysStatusRaw?.syncInventoryState;

  if (
    onboardingStatus === ONBOARDING_STARTED &&
    syncOrderState === ONBOARDING_NOT_STARTED &&
    syncInventoryState === ONBOARDING_NOT_STARTED
  ) {
    return baseMessage;
  }

  if (
    syncOrderState === ONBOARDING_STARTED ||
    syncInventoryState === ONBOARDING_STARTED
  ) {
    return `${baseMessage}\nWe are pulling last 7 days of orders`;
  }

  if (
    (syncOrderState === ONBOARDING_COMPLETED ||
      syncOrderState === COMPLETED) &&
    (syncInventoryState === ONBOARDING_COMPLETED ||
      syncInventoryState === COMPLETED)
  ) {
    const asyncOrderState = sync60DaysStatusRaw?.asyncOrderState;
    const asyncInventoryState = sync60DaysStatusRaw?.asyncInventoryState;

    if (
      asyncOrderState === ONBOARDING_NOT_STARTED &&
      asyncInventoryState === ONBOARDING_NOT_STARTED
    ) {
      return `${baseMessage}\nFinished pulling 7 days of orders`;
    }

    if (
      asyncOrderState === ONBOARDING_STARTED ||
      asyncInventoryState === ONBOARDING_STARTED
    ) {
      return `${baseMessage}\nWe are pulling orders older than 60 days`;
    }

    if (
      (asyncOrderState === ONBOARDING_COMPLETED ||
        asyncOrderState === COMPLETED) &&
      (asyncInventoryState === ONBOARDING_COMPLETED ||
        asyncInventoryState === COMPLETED)
    ) {
      if (
        userDetails?.isAuthorizeHistoricalOrders &&
        historicalJobStatusObject?.status === true
      ) {
        return `${baseMessage}\n${
          historicalJobStatusObject?.message ||
          "We are pulling historical orders"
        }`;
      }
      return null;
    }
  }

  return baseMessage;
};

/**
 * Same 7/60 visibility gate as OnboardingProgressBanner.shouldShowOnboardingProgress.
 */
export const shouldShowOnboardingProgress = ({
  userDetails,
  onboardingSyncJobStatus,
  historicalJobStatusObject,
  sync7DaysStatusRaw,
  sync60DaysStatusRaw,
  currentActiveRoute,
}) => {
  const onboardingStatus =
    sync7DaysStatusRaw?.onboardingStatus ||
    sync60DaysStatusRaw?.onboardingStatus;
  if (
    onboardingStatus === COMPLETED ||
    onboardingStatus === ONBOARDING_COMPLETED
  ) {
    return false;
  }

  if (currentActiveRoute === "incomingInventoryPage") {
    return false;
  }

  if (
    userDetails?.isAuthorizeHistoricalOrders &&
    historicalJobStatusObject?.status === true
  ) {
    return false;
  }

  if (onboardingStatus === ONBOARDING_STARTED) {
    return true;
  }

  return onboardingSyncJobStatus === false;
};

/**
 * True when the header progress bar would show for onboarding 7/60 or
 * historical import. Location-switch and Incoming Inventory bars are separate.
 *
 * Example: historicalJobStatusObject.status === true, or 7/60 bar copy is showing.
 */
export const isOnboardingProgressBarVisible = ({
  userDetails,
  onboardingSyncJobStatus,
  historicalJobStatusObject,
  isOnboardingCompleted,
  sync7DaysStatusRaw,
  sync60DaysStatusRaw,
  currentActiveRoute,
}) => {
  if (isOnboardingCompleted) {
    return false;
  }

  const isHistoricalAuthorized =
    userDetails?.isAuthorizeHistoricalOrders === true;
  const isHistoricalRunning =
    isHistoricalAuthorized && historicalJobStatusObject?.status === true;

  if (isHistoricalAuthorized) {
    return isHistoricalRunning;
  }

  const progressArgs = {
    userDetails,
    onboardingSyncJobStatus,
    historicalJobStatusObject,
    sync7DaysStatusRaw,
    sync60DaysStatusRaw,
    currentActiveRoute,
  };

  return (
    shouldShowOnboardingProgress(progressArgs) &&
    getOnboardingProgressMessage(progressArgs) !== null
  );
};
